import {
  appAnalysisCompareResponseSchema,
  appAnalysisResultResponseSchema,
  listAppAnalysisResultsResponseSchema,
  listAppAnalysisRunLogsResponseSchema,
  qlikEvaluationRankingResponseSchema,
  type AnalysisDiff,
  type AppAnalysisDetail,
  type AppAnalysisListItem,
  type AppAnalysisRunLog,
  type AppAnalysisRunLogLevel,
  type AppAnalysisStatus,
  type QlikEvaluationRankingItem,
  type StartAppAnalysisInput,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequest, apiRequestJson, apiDownload } from "../api-client";

export async function listAppAnalyses(
  params?: {
    connectionId?: string;
    appId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    cursor?: string;
    status?: AppAnalysisStatus;
  },
  options?: { signal?: AbortSignal },
): Promise<{ data: AppAnalysisListItem[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.connectionId) search.set("connectionId", params.connectionId);
  if (params?.appId) search.set("appId", params.appId);
  if (params?.dateFrom) search.set("dateFrom", params.dateFrom);
  if (params?.dateTo) search.set("dateTo", params.dateTo);
  if (params?.status) search.set("status", params.status);

  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${API_V1_PREFIX}/analyses${qs}`,
    responseSchema: listAppAnalysisResultsResponseSchema,
    badResponseMessage: "Failed to fetch analyses",
    signal: options?.signal,
  });
}

// Run-first trigger (Option B): creates the lifecycle placeholder + worker job,
// returns the analysis so the UI can navigate to it immediately.
export async function startAppAnalysis(input: StartAppAnalysisInput): Promise<AppAnalysisDetail> {
  const res = await apiRequestJson({
    path: `${API_V1_PREFIX}/analyses`,
    method: "POST",
    body: input,
    responseSchema: appAnalysisResultResponseSchema,
    badResponseMessage: "Failed to start analysis",
  });
  return res.data;
}

export function downloadAppAnalysisRunLogs(id: string): Promise<void> {
  return apiDownload(`${API_V1_PREFIX}/analyses/${id}/logs/export`, `analysis-logs-${id}.txt`);
}

export async function listAppAnalysisRunLogs(
  id: string,
  params?: { limit?: number; cursor?: string; level?: AppAnalysisRunLogLevel },
): Promise<{ data: AppAnalysisRunLog[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.level) search.set("level", params.level);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${API_V1_PREFIX}/analyses/${id}/logs${qs}`,
    responseSchema: listAppAnalysisRunLogsResponseSchema,
    badResponseMessage: "Failed to load analysis logs",
  });
}

export async function getAppAnalysis(id: string): Promise<AppAnalysisDetail> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/analyses/${id}`,
    responseSchema: appAnalysisResultResponseSchema,
    badResponseMessage: "Failed to fetch analysis",
  });
  return response.data;
}

// On-demand diff against another analysis of the same app. The service orders
// the pair by `analyzedAt`, so the ids may be passed in either order.
export async function compareAppAnalyses(id: string, baseId: string): Promise<AnalysisDiff> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/analyses/${id}/compare?baseId=${encodeURIComponent(baseId)}`,
    responseSchema: appAnalysisCompareResponseSchema,
    badResponseMessage: "Failed to compare analyses",
  });
  return response.data;
}

// Latest completed Qlik evaluation per app on one connection, slowest cold
// sheet first.
export async function listQlikEvaluationRanking(
  connectionId: string,
): Promise<QlikEvaluationRankingItem[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/analyses/evaluations/ranking?connectionId=${encodeURIComponent(connectionId)}`,
    responseSchema: qlikEvaluationRankingResponseSchema,
    badResponseMessage: "Failed to load the Qlik evaluation ranking",
  });
  return response.data;
}

export async function deleteAppAnalysis(id: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/analyses/${id}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}
